import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Quote,
  Star,
  ArrowLeft,
  ArrowRight,
  Filter,
} from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import { SITE } from "../site.config";
import { REVIEWS, TOTAL_REVIEWS } from "../data/reviews";
import useDocumentMeta from "../hooks/useDocumentMeta";

// Per-review JSON-LD schemas slow down crawlers if we emit all 95+;
// 25 is the sweet spot Google guidance recommends.
const REVIEW_SCHEMA_LIMIT = 25;

export default function ReviewsPage() {
  const [filter, setFilter] = useState("All");

  const services = useMemo(() => {
    const set = new Set(REVIEWS.map((r) => r.service));
    return ["All", ...Array.from(set).sort()];
  }, []);

  const filtered = useMemo(
    () =>
      filter === "All"
        ? REVIEWS
        : REVIEWS.filter((r) => r.service === filter),
    [filter]
  );

  // Per-review schema is allowed; aggregateRating must come from Google itself.
  const reviewSchemas = REVIEWS.slice(0, REVIEW_SCHEMA_LIMIT).map((r) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "AutoRepair",
      name: SITE.business.fullName,
      url: "https://powerzautoelectrical.com.au/",
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: "5",
      bestRating: "5",
    },
    author: { "@type": "Person", name: r.name },
    reviewBody: r.quote,
  }));

  useDocumentMeta({
    title: `Customer Reviews — ${TOTAL_REVIEWS}+ Google Reviews | ${SITE.business.name}`,
    description: `Read ${TOTAL_REVIEWS}+ verified Google reviews for Powerz Auto Electrical & Airconditioning — Sunshine Coast's trusted auto electrician. Real customer stories about 4WD fitouts, towing electrics, A/C, dual battery and diagnostics work.`,
    canonical: "https://powerzautoelectrical.com.au/reviews",
    image: SITE.business.logo,
    jsonLd: reviewSchemas,
  });

  return (
    <div className="bg-black text-white">
      <Header />

      {/* Hero */}
      <section
        data-testid="reviews-hero"
        className="relative w-full overflow-hidden bg-black pt-40 md:pt-48 pb-16"
      >
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
          style={{
            background: "radial-gradient(circle, #5EE6E6, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 grain" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
          <Link
            to="/"
            data-testid="reviews-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            BACK TO HOME
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              CUSTOMER REVIEWS
            </span>
          </div>

          <h1
            data-testid="reviews-headline"
            className="bebas text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-4xl"
          >
            {TOTAL_REVIEWS}+ REAL <br />
            <span className="text-cyan-pz glow-cyan">GOOGLE REVIEWS.</span>
          </h1>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center gap-2 bg-cyan-pz/10 border border-cyan-pz/40 px-5 py-3 clip-tag">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-cyan-pz text-cyan-pz"
                  />
                ))}
              </div>
              <span className="bebas text-white tracking-widest">
                {TOTAL_REVIEWS}+ Reviews
              </span>
            </div>
            <a
              href={SITE.business.googleProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-on-google-link"
              className="bebas text-cyan-pz hover:text-white tracking-widest text-sm inline-flex items-center gap-2"
            >
              View on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <p className="mt-8 text-white/70 barlow text-base md:text-lg max-w-3xl leading-relaxed">
            Every review below is a real Google review from a real Sunshine
            Coast customer. We've never paid for a review, never edited one,
            and never deleted one we didn't like.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="relative bg-[#0A0A0A] border-y border-white/10 py-5 sticky top-24 md:top-32 lg:top-40 z-30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap items-center gap-2">
          <span className="hidden md:inline-flex items-center gap-2 text-white/50 bebas tracking-widest text-sm mr-2">
            <Filter className="w-4 h-4" />
            FILTER BY SERVICE
          </span>
          {services.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`reviews-filter-${s
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`}
              className={`bebas tracking-widest text-xs px-4 py-2 transition-all border ${
                filter === s
                  ? "bg-cyan-pz text-black border-cyan-pz"
                  : "bg-transparent text-white/80 border-white/15 hover:border-cyan-pz hover:text-cyan-pz"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      {/* Reviews grid */}
      <section className="relative bg-black py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="reviews-grid"
          >
            {filtered.map((r, i) => (
              <article
                key={`${r.name}-${i}`}
                data-testid={`review-${i}`}
                className="relative bg-[#0A0A0A] border border-white/10 hover:border-cyan-pz transition-colors p-6 clip-perf flex flex-col"
              >
                <Quote className="w-8 h-8 text-cyan-pz/30 mb-3" />
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, s) => (
                    <Star
                      key={s}
                      className="w-3.5 h-3.5 fill-cyan-pz text-cyan-pz"
                    />
                  ))}
                </div>
                <p className="text-white/80 barlow text-sm leading-relaxed mb-4 flex-1">
                  "{r.quote}"
                </p>
                <div className="border-t border-white/10 pt-3">
                  <p className="bebas text-white tracking-wider text-base">
                    {r.name}
                  </p>
                  <p className="text-cyan-pz/80 text-xs barlow tracking-wider uppercase">
                    {r.service} • via Google
                  </p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-16 text-center border-t border-white/10 pt-12">
            <h3 className="bebas text-white text-3xl md:text-5xl tracking-wide mb-4">
              READY TO BE THE NEXT REVIEW?
            </h3>
            <p className="text-white/60 barlow mb-8 max-w-xl mx-auto">
              Whether it's a quick diagnosis or a full 4WD auto electrical fitout — give
              us a call and we'll sort it.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${SITE.business.phoneTel}`}
                className="inline-flex items-center justify-center gap-2 bg-cyan-pz text-black px-7 py-4 bebas tracking-widest hover:bg-white transition-colors clip-perf"
              >
                CALL {SITE.business.phone}
              </a>
              <a
                href={`mailto:${SITE.business.email}?subject=Powerz%20Auto%20Electrical%20Enquiry`}
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-7 py-4 bebas tracking-widest hover:border-cyan-pz hover:text-cyan-pz transition-colors"
              >
                EMAIL US
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
